import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ChangeLogMonitorService } from '../../services/change-log-monitor.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-change-log-monitor',
    templateUrl: './change-log-monitor.component.html',
    styleUrls: ['./change-log-monitor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChangeLogMonitorComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Component properties
  modalTitle = "View Message";
  minimise = false;
  showJsonLoader = false;
  monitoringData: SafeHtml = '';

  // Data properties
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeLogMonitorService: ChangeLogMonitorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.fetchMonitoringData();
      }
    } catch (e) {
      console.error('Error in ChangeLogMonitor init:', e);
      this.showJsonLoader = false;
    }
  }

  fetchMonitoringData() {
    this.showJsonLoader = true;
    this.cdr.detectChanges();

    const gp = this.globalService?.globalParams || {};

    // Build params using real data from rowData and headerData
    const params = {
      tax_year: gp.tax_year || this.filterParams.tax_year,
      client_key: this.USER_SETTINGS?.user?.client_key,
      le_key: this.rowData.LE_KEY,
      le_type: this.rowData.LE_TYPE,
      submissionId: this.rowData.SUBMISSION_ID
    };

    this.changeLogMonitorService.getMonitoringData(
      this.GENERAL_CONFIG.bulk_pdf_engine_api_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showJsonLoader = false;

        if (response.callSuccess == "1") {
          if (response.snsDetails) {
            try {
              const tempMonitoringData = safeJsonInput<any>(response.snsDetails.additional_DATA, null, 'change-log-monitor.parse1');
              // Convert object to formatted JSON view
              const jsonView = this.objToJsonView(tempMonitoringData);
              this.monitoringData = this.sanitizer.bypassSecurityTrustHtml(jsonView);
              this.cdr.detectChanges();
            } catch (e) {
              console.error('Error parsing SNS details:', e);
              this._snackBar.open('Error parsing SNS message data', 'Close', {
                duration: 4000,
                panelClass: ['error-snackbar']
              });
              // Delay to ensure AngularJS event listener is attached (500ms timeout in controller)
              setTimeout(() => {
                this.cancel();
              }, 600);
            }
          } else {
            this._snackBar.open('No sns msg found', 'Close', {
              duration: 4000,
              panelClass: ['warning-snackbar']
            });
          }
        } else {
          // callSuccess is not "1" - this is an error
          this._snackBar.open('Error loading SNS message', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          // Delay to ensure AngularJS event listener is attached (500ms timeout in controller)
          setTimeout(() => {
            this.cancel();
          }, 600);
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching monitoring data:', error);
        this.showJsonLoader = false;
        this._snackBar.open('Error loading SNS message', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        // Delay to ensure AngularJS event listener is attached (500ms timeout in controller)
        setTimeout(() => {
          this.cancel();
        }, 600);
        this.cdr.detectChanges();
      }
    );
  }

  // Convert object to JSON view (similar to ObjectDiff.objToJsonView in AngularJS)
  objToJsonView(obj: any): string {
    return '<pre>' + JSON.stringify(obj, null, 2) + '</pre>';
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
